
import React from 'react';
import { CircleDecoration, WaveDivider } from '../Animations';
import ImageOptimizer from '../ImageOptimizer';
import OptimizedFadeIn from '../OptimizedFadeIn';

interface TestimonialCardProps {
  initials: string;
  name: string;
  quote: string;
  role: string;
  imageSrc?: string;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({
  initials,
  name,
  quote,
  role,
  imageSrc
}) => (
  <OptimizedFadeIn className="testimonial-item">
    <div className="bg-[#F9F5F0] rounded-3xl p-8 shadow-sm relative group hover:shadow-md transition-all">
      <div className="flex flex-col">
        <div className="flex items-center mb-4">
          {imageSrc ? (
            <div className="w-14 h-14 mr-4 rounded-full overflow-hidden">
              <ImageOptimizer 
                src={imageSrc} 
                alt={`${name} testimonial`} 
                className="w-14 h-14 object-cover" 
                width={56} 
                height={56} 
                priority={true}
              />
            </div>
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#E27D60]/20 flex items-center justify-center mr-4 group-hover:bg-[#E27D60]/30 transition-colors">
              <span className="text-[#E27D60] font-semibold">{initials}</span>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-hormony-charcoal">{name}</h3>
            <p className="text-sm text-hormony-charcoal/60">{role}</p>
            <div className="flex mt-1">
              {[...Array(5)].map((_, i) => (
                <svg 
                  key={i} 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="#E27D60" 
                  className="mr-1"
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              ))}
            </div>
          </div>
        </div>
        <p className="text-hormony-charcoal/80 italic">{quote}</p>
      </div>
    </div>
  </OptimizedFadeIn>
);

const TestimonialsSection: React.FC = () => {
  const testimonials = [
    {
      initials: 'SJ',
      name: 'Sarah Johnson',
      role: 'Busy Mom & Professional',
      quote: '"After struggling with fatigue and mood swings for years, this program gave me my life back. My energy levels are consistent, and I finally feel like myself again. The 3-minute daily practices are perfect for my busy schedule."'
    },
    {
      initials: 'MR',
      name: 'Michelle Rodriguez',
      role: 'Health Coach',
      quote: '"As someone who\'s tried everything for hormone balance, this program is different. It\'s comprehensive yet simple, and the results are remarkable. My clients love it too—it\'s science-backed and actually works!"'
    }
  ];
  
  return (
    <section className="bg-white py-20 relative">
      <WaveDivider position="top" color="#F9F5F0" className="absolute top-0 left-0 w-full" />
      <CircleDecoration size={250} color="rgba(226, 125, 96, 0.05)" className="absolute top-40 -left-20 z-0" />
      
      <div className="container mx-auto px-4 relative z-10 mt-24 py-[130px]">
        <OptimizedFadeIn>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block px-4 py-1 rounded-full bg-[#E27D60]/10 text-[#E27D60] text-sm font-medium mb-4">
              Success Stories
            </span>
            <h2 className="text-3xl font-display font-semibold text-hormony-charcoal mb-4">
              Exclusive Results from Our Community
            </h2>
            <p className="text-lg text-hormony-charcoal/70">
              Hear from our exclusive members who've experienced remarkable transformations through our invitation-only program.
            </p>
          </div>
        </OptimizedFadeIn>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard 
              key={index} 
              initials={testimonial.initials} 
              name={testimonial.name} 
              role={testimonial.role}
              quote={testimonial.quote} 
            />
          ))}
        </div>
      </div>
      
      <div className="absolute bottom-0 left-0 w-full">
        <WaveDivider color="#E27D60" />
      </div>
    </section>
  );
};

export default React.memo(TestimonialsSection);
